import { useState } from 'react';
import { Check, ChevronsUpDown, Building2, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useUserProfile, useOrganization, useUserOrganizations, useSwitchOrganization } from '@/hooks/useUserProfile';
import { Skeleton } from '@/components/ui/skeleton';
import { useNavigate } from 'react-router-dom';

export function OrganizationSwitcher() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  
  const { data: profile } = useUserProfile();
  const { data: currentOrg, isLoading: orgLoading } = useOrganization();
  const { data: userOrgs = [], isLoading: orgsLoading } = useUserOrganizations();
  const switchOrg = useSwitchOrganization();

  const handleSelect = (orgId: string) => {
    if (orgId !== profile?.organization_id) {
      switchOrg.mutate(orgId);
    }
    setOpen(false);
  };

  const handleCreateNew = () => {
    setOpen(false);
    navigate('/onboarding');
  };

  if (orgLoading || orgsLoading) {
    return <Skeleton className="h-10 w-48" />;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2 truncate">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{currentOrg?.name || 'Sélectionner une organisation'}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder="Rechercher..." />
          <CommandList>
            <CommandEmpty>Aucune organisation trouvée.</CommandEmpty>
            <CommandGroup heading="Vos organisations">
              {userOrgs.map((userOrg: any) => {
                const org = userOrg.organizations;
                if (!org) return null;
                return (
                  <CommandItem
                    key={org.id}
                    value={org.name}
                    onSelect={() => handleSelect(org.id)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        profile?.organization_id === org.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{org.name}</span>
                      <span className="text-xs text-muted-foreground capitalize">{userOrg.role}</span>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem onSelect={handleCreateNew} className="cursor-pointer">
                <Plus className="mr-2 h-4 w-4" />
                <span>Créer une organisation</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
